import {
  IonButton,
  IonCol,
  IonIcon,
  IonRow,
  IonToast,
  IonLoading,
} from '@ionic/react';
import { chevronForward } from 'ionicons/icons';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { config } from '../../../config';
import { CrateService } from '../../../services/Crate.service';
import PostHogService from '../../../services/PostHog.service';
import {
  setCreatedFrom,
  setSelectedCrate,
  setSelectedCrateName,
} from '../../../store/slices/crate.slice';
import { RootState } from '../../../store/store';
import { iCrate } from '../../../types';
import isElectron from '../../../utils/isElectron';
import { useToast } from '../../ToastManager/useToast';
import style from './build-generate-crate.module.scss';

interface GenerateCrateProps {
  buildCrateFn: () => iCrate;
  isBtnDisabled?: boolean;
  disabledMessage?: string;
  compactFooter?: boolean;
}

export default function BuildGenerateCrate({
  buildCrateFn,
  isBtnDisabled = true,
  disabledMessage = '',
  compactFooter = false,
}: GenerateCrateProps) {
  const btnClass = `${style['generate-crate-btn']} ${
    isBtnDisabled ? style['disabled'] : ''
  }`;
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const { errorToast } = useToast();

  // Check if persistent player is visible
  const persistentPlayerVisible = useSelector(
    (state: RootState) => state.audioPlayerState.persistentPlayerVisible,
  );

  const saveToMyCrates = (crate: iCrate) => {
    if (crate) {
      setIsLoading(true);
      try {
        if (PostHogService.isInitialized()) {
          PostHogService.capture('crate_generation_started', {
            build_method: crate?.createdFrom || 'unknown',
            track_count: crate.Tracks?.length || 0,
            has_image: !!crate.Image,
            crate_title_length: crate.Title?.length || 0,
            is_electron: isElectron(),
            app_version: config.VERSION,
          });
        }
      } catch (error) {
        console.error(
          '[BuildGenerateCrate] Error capturing start event:',
          error,
        );
      }
      CrateService.createMyCrate(crate)
        .then((response) => {
          if (!response?.id) {
            throw new Error(
              response?.message || 'Invalid response from server',
            );
          }
          // PostHog Crate Created Event
          try {
            if (PostHogService.isInitialized()) {
              PostHogService.capture('crate_created', {
                crate_id: response.id,
                crate_title: crate.Title,
                build_method: crate?.createdFrom || 'unknown',
                track_count: crate.Tracks?.length || 0,
                has_image: !!crate.Image,
                has_description: !!crate.Description,
                is_electron: isElectron(),
                app_version: config.VERSION,
                timestamp: new Date().toISOString(),
              });
            }
          } catch (error) {
            console.error(
              '[BuildGenerateCrate] Error capturing PostHog event:',
              error,
            );
          }

          dispatch(setSelectedCrate(crate));
          dispatch(setCreatedFrom(crate?.createdFrom || ''));
          dispatch(setSelectedCrateName(crate.Title));
          history.push(`/my-crates/crate/${response.id}/${crate.createdFrom}`);
        })
        .catch((error) => {
          let message =
            error?.response?.data?.message ||
            error?.data?.message ||
            error?.message ||
            'Failed to create crate';
          if (
            message.includes('array length exceeds') ||
            message.includes('200')
          ) {
            message =
              'This crate has more than 200 tracks. Please reduce the playlist size and try again.';
          }
          errorToast({ message });
          try {
            if (PostHogService.isInitialized()) {
              PostHogService.capture('crate_creation_failed', {
                build_method: crate?.createdFrom || 'unknown',
                track_count: crate.Tracks?.length || 0,
                error_message: message,
                error_type: error?.message ? 'api_error' : 'unknown_error',
                is_electron: isElectron(),
                app_version: config.VERSION,
              });
            }
          } catch (err) {
            console.error(
              '[BuildGenerateCrate] Error capturing PostHog event:',
              err,
            );
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  function generateCrate() {
    if (isBtnDisabled) {
      setErrorToastOpen(true);
    }
    if (!isBtnDisabled) {
      const crateData = buildCrateFn();
      saveToMyCrates(crateData);
    }
  }

  return (
    <div
      className={`${style['generate-crate-ctn']} ${
        compactFooter ? style['compact-footer'] : ''
      } ${persistentPlayerVisible ? style['with-persistent-player'] : ''}`}
    >
      <IonRow className={style['full-width']}>
        <IonCol
          size="12"
          sizeLg="6"
          offsetLg="6"
          className={`${style['align-center']} ${style['full-width']}`}
        >
          <IonButton
            onClick={generateCrate}
            disabled={isBtnDisabled || isLoading}
          >
            {isLoading ? 'Building Crate...' : 'Next'}
            {!isLoading && <IonIcon icon={chevronForward} slot="end" />}
          </IonButton>
        </IonCol>
      </IonRow>
      <IonToast
        isOpen={errorToastOpen}
        message={
          disabledMessage !== ''
            ? disabledMessage
            : 'Please make sure to provide a Crate Name and have tracks to start building'
        }
        onDidDismiss={() => setErrorToastOpen(false)}
        position="middle"
        duration={5000}
      ></IonToast>
      <IonLoading
        isOpen={isLoading}
        message="Building your crate..."
        spinner="crescent"
      />
    </div>
  );
}
